/**
 * User Role Utilities
 * 
 * Helper functions for role-based access control
 */

import { getSql } from '@/lib/db';

export type MemberRole = 'owner' | 'admin' | 'member';

export interface UserMembership {
  orgId: string;
  orgName: string;
  role: MemberRole;
}

/**
 * Role hierarchy for permission checks
 * Higher number = more permissions
 */
export const ROLE_HIERARCHY: Record<MemberRole, number> = {
  owner: 3,
  admin: 2,
  member: 1,
};

/**
 * Permission definitions by role
 */
export const ROLE_PERMISSIONS = {
  owner: {
    billing: { view: true, manage: true },
    team: { view: true, invite: true, remove: true, changeRoles: true },
    settings: { view: true, edit: true },
    subscription: { view: true, upgrade: true, cancel: true },
  },
  admin: {
    billing: { view: true, manage: true },
    team: { view: true, invite: true, remove: true, changeRoles: false },
    settings: { view: true, edit: true },
    subscription: { view: true, upgrade: false, cancel: false },
  },
  member: {
    billing: { view: false, manage: false },
    team: { view: true, invite: false, remove: false, changeRoles: false },
    settings: { view: true, edit: false },
    subscription: { view: false, upgrade: false, cancel: false },
  },
} as const;

/**
 * Get user's membership and role for their organization
 */
export async function getUserMembership(userEmail: string): Promise<UserMembership | null> {
  const sql = getSql();

  const memberships = await sql`
    SELECT m."orgId", o.name as "orgName", m.role
    FROM memberships m
    JOIN orgs o ON o.id = m."orgId"
    JOIN users u ON u.id = m."userId"
    WHERE u.email = ${userEmail}
    ORDER BY m."createdAt" DESC
    LIMIT 1
  `;

  if (memberships.length === 0) {
    return null;
  }

  return {
    orgId: memberships[0].orgId,
    orgName: memberships[0].orgName,
    role: (memberships[0].role as MemberRole) || 'member',
  };
}

/**
 * Check if a role has a specific permission
 */
export function hasPermission(
  role: MemberRole,
  resource: keyof typeof ROLE_PERMISSIONS.owner,
  action: string
): boolean {
  const permissions = ROLE_PERMISSIONS[role];
  if (!permissions) return false;
  
  const resourcePermissions = permissions[resource] as Record<string, boolean>;
  if (!resourcePermissions) return false;
  
  return resourcePermissions[action] === true;
}

/**
 * Check if role A has equal or higher permissions than role B
 */
export function hasRoleOrHigher(userRole: MemberRole, requiredRole: MemberRole): boolean {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
}

/**
 * Check if user can access billing
 */
export function canAccessBilling(role: MemberRole): boolean {
  return hasPermission(role, 'billing', 'view');
}

/**
 * Check if user can manage billing (upgrade, cancel, etc.)
 */
export function canManageBilling(role: MemberRole): boolean {
  return hasPermission(role, 'billing', 'manage');
}

/**
 * Check if user can manage team
 */
export function canManageTeam(role: MemberRole): boolean {
  return hasPermission(role, 'team', 'invite');
}

/**
 * Get display label for a role
 */
export function getRoleLabel(role: MemberRole): string {
  const labels: Record<MemberRole, string> = {
    owner: 'Owner',
    admin: 'Admin',
    member: 'Member',
  };
  return labels[role] || 'Member';
}

/**
 * Get role badge color for UI
 */
export function getRoleBadgeColor(role: MemberRole): string {
  const colors: Record<MemberRole, string> = {
    owner: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    admin: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    member: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30',
  };
  return colors[role] || colors.member;
}
